import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Cpu, Key, KeyRound, CheckCircle2, AlertCircle, Loader2, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AdminShell } from './admin-shell';
import { cn } from '@/lib/utils';

interface ProviderModel {
  id: string;
  name: string;
  pricePerMInput: number;
  pricePerMOutput: number;
  pricePerMCacheWrite?: number;
  pricePerMCacheRead?: number;
}

interface ProviderRow {
  provider: 'anthropic' | 'bedrock' | 'vertex';
  name: string;
  description: string;
  keyLabel: string;
  keyHint: string;
  models: ProviderModel[];
  enabled: boolean;
  allowedModels: string[];
  defaultModel: string | null;
  platformKeyConfigured: boolean;
  notes: string | null;
}

export function AdminProvidersPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'providers'],
    queryFn: () => apiClient.adminListProviders(),
  });
  const providers = (data?.data as ProviderRow[] | undefined) ?? [];

  return (
    <AdminShell subtitle="Toggle providers, curate models, set platform default keys. Org admins pick from these at runtime.">
      <div className="flex items-center gap-3">
        <Cpu className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-2xl font-bold text-foreground">LLM Providers</h1>
      </div>

      {isLoading && (
        <Card className="p-8 text-center text-muted-foreground">Loading…</Card>
      )}

      <div className="space-y-5">
        {providers.map((p) => <ProviderCard key={p.provider} provider={p} />)}
      </div>
    </AdminShell>
  );
}

function ProviderCard({ provider }: { provider: ProviderRow }) {
  const qc = useQueryClient();
  // Filter stored IDs against the current catalog. After a catalog
  // refresh, the DB might still hold IDs that no longer exist; we
  // shouldn't carry those into the next save (the API would drop them
  // anyway, but keeping the local state honest avoids surprises in the
  // dirty-flag and "X of Y" labels).
  const knownIds = new Set(provider.models.map((m) => m.id));
  const cleanAllowed = (provider.allowedModels ?? []).filter((id) => knownIds.has(id));
  const cleanDefault = provider.defaultModel && knownIds.has(provider.defaultModel) ? provider.defaultModel : null;

  const [enabled, setEnabled] = useState(provider.enabled);
  const [allowedModels, setAllowedModels] = useState<string[]>(cleanAllowed);
  const [defaultModel, setDefaultModel] = useState<string | null>(cleanDefault);
  const [notes, setNotes] = useState(provider.notes ?? '');
  const [keyValue, setKeyValue] = useState('');
  const [showKey, setShowKey] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'providers'] });

  const update = useMutation({
    mutationFn: () => apiClient.adminUpdateProvider(provider.provider, {
      enabled,
      allowedModels,
      defaultModel,
      notes: notes.trim() || null,
    }),
    onSuccess: () => { toast.success(`${provider.name} updated`); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });

  const setKey = useMutation({
    mutationFn: () => apiClient.adminSetProviderKey(provider.provider, keyValue),
    onSuccess: () => { toast.success(`${provider.name} key saved`); setKeyValue(''); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });

  const clearKey = useMutation({
    mutationFn: () => apiClient.adminSetProviderKey(provider.provider, ''),
    onSuccess: () => { toast.success(`${provider.name} key cleared`); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });

  function toggleModel(id: string) {
    const next = allowedModels.includes(id)
      ? allowedModels.filter((x) => x !== id)
      : [...allowedModels, id];
    setAllowedModels(next);
    // If we just removed the default model, clear it.
    if (defaultModel === id && !next.includes(id)) setDefaultModel(null);
  }

  const dirty =
    enabled !== provider.enabled ||
    JSON.stringify(allowedModels.sort()) !== JSON.stringify((provider.allowedModels ?? []).slice().sort()) ||
    defaultModel !== provider.defaultModel ||
    (notes.trim() || null) !== provider.notes;

  return (
    <Card className="p-0 overflow-hidden">
      <div className={cn(
        'px-5 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap',
        enabled ? 'bg-india-green/5' : 'bg-muted/30',
      )}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-foreground">{provider.name}</h2>
            <span className={cn(
              'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full',
              enabled
                ? 'bg-india-green/15 text-india-green'
                : 'bg-muted text-muted-foreground',
            )}>
              {enabled ? 'enabled' : 'disabled'}
            </span>
            {provider.platformKeyConfigured ? (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> platform key set
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-600 dark:text-yellow-500 inline-flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> no platform key
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{provider.description}</p>
        </div>
        <label className="text-sm font-medium inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="rounded border-border"
          />
          Enabled
        </label>
      </div>

      <div className="px-5 py-4 space-y-5">
        {/* Allowed models */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Allowed models <span className="normal-case font-normal">({allowedModels.length} of {provider.models.length})</span>
            </p>
            <div className="flex gap-2">
              <button onClick={() => setAllowedModels(provider.models.map((m) => m.id))}
                className="text-[11px] text-muted-foreground hover:text-foreground">select all</button>
              <span className="text-[11px] text-muted-foreground">·</span>
              <button onClick={() => { setAllowedModels([]); setDefaultModel(null); }}
                className="text-[11px] text-muted-foreground hover:text-foreground">clear</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {provider.models.map((m) => {
              const checked = allowedModels.includes(m.id);
              const isDefault = defaultModel === m.id;
              return (
                <label key={m.id}
                  className={cn(
                    'flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors',
                    checked
                      ? 'border-primary/40 bg-primary/5'
                      : 'border-border hover:bg-muted/40',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleModel(m.id)}
                    className="mt-1 rounded border-border"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{m.name}</span>
                      {checked && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setDefaultModel(isDefault ? null : m.id); }}
                          className={cn(
                            'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full',
                            isDefault
                              ? 'bg-saffron/20 text-saffron'
                              : 'bg-muted text-muted-foreground hover:bg-muted/70',
                          )}
                        >
                          {isDefault ? 'default' : 'set default'}
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono truncate">{m.id}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      ${m.pricePerMInput}/M in · ${m.pricePerMOutput}/M out
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Notes</p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Visible to platform admins only."
            className="w-full text-sm border border-border rounded-md p-2 bg-background"
            rows={2}
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={() => update.mutate()} disabled={!dirty || update.isPending}>
            {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </div>

      {/* Platform key */}
      <div className="px-5 py-4 border-t border-border bg-muted/20">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold">Platform default key</p>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Used for any org that hasn't BYOK'd this provider. Stored in AWS Secrets Manager — never echoed back.
        </p>
        <div className="flex items-stretch gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px] relative">
            <Key className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type={showKey ? 'text' : 'password'}
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              placeholder={provider.platformKeyConfigured ? '••••••• (key already set — paste to replace)' : provider.keyHint}
              className="pl-8 pr-9"
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <Button onClick={() => setKey.mutate()} disabled={!keyValue.trim() || setKey.isPending}>
            {setKey.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {provider.platformKeyConfigured ? 'Replace key' : 'Save key'}
          </Button>
          {provider.platformKeyConfigured && (
            <Button
              variant="outline"
              onClick={() => {
                if (confirm(`Clear platform key for ${provider.name}? Orgs using the platform default will no longer be able to run agents on this provider.`)) {
                  clearKey.mutate();
                }
              }}
              disabled={clearKey.isPending}
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
